import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import Lenis from "lenis";
import { GraduationCap, Bot } from "lucide-react";
import { AuthProvider } from "@/context/AuthContext";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import DashboardHome from "@/pages/dashboard/DashboardHome";
import MonParcours from "@/pages/dashboard/MonParcours";
import MesDocuments from "@/pages/dashboard/MesDocuments";
import MesEcheances from "@/pages/dashboard/MesEcheances";
import Parametres from "@/pages/dashboard/Parametres";
import ChatAgent from "@/pages/dashboard/ChatAgent";
import ExamenQuiz from "@/pages/dashboard/ExamenQuiz";
import Formations from "@/pages/dashboard/Formations";
import Messages from "@/pages/dashboard/Messages";
import Interprete from "@/pages/dashboard/Interprete";
import AdminSuggestions from "@/pages/dashboard/AdminSuggestions";
import GuideRenouvellement from "@/pages/dashboard/GuideRenouvellement";
import PartnerSignupPage from "@/pages/PartnerSignupPage";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminPremium from "@/pages/admin/AdminPremium";
import AdminContents from "@/pages/admin/AdminContents";
import AdminQuestions from "@/pages/admin/AdminQuestions";
import AdminPartners from "@/pages/admin/AdminPartners";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminAI from "@/pages/admin/AdminAI";
import AdminModeration from "@/pages/admin/AdminModeration";
import AdminSecurity from "@/pages/admin/AdminSecurity";
import AdminSettings from "@/pages/admin/AdminSettings";
import EspacePartenaire from "@/pages/dashboard/EspacePartenaire";
import Partenaires from "@/pages/dashboard/Partenaires";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import LegalPage from "@/pages/LegalPage";
import ContactPage from "@/pages/ContactPage";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";

const COACH_ACCENT = {
  button: "bg-bleu hover:bg-bleu-dark", userBubble: "bg-bleu", cursor: "bg-bleu",
  iconBg: "bg-bleu-light", iconColor: "text-bleu", bar: "bg-bleu",
  activeSession: "border-bleu/30 bg-bleu-light text-bleu",
};
const DEMARCHES_ACCENT = {
  button: "bg-emeraude hover:bg-emerald-700", userBubble: "bg-emeraude", cursor: "bg-emeraude",
  iconBg: "bg-emerald-50", iconColor: "text-emeraude", bar: "bg-emeraude",
  activeSession: "border-emeraude/30 bg-emerald-50 text-emeraude",
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<AuthPage mode="login" />} />
          <Route path="/signup" element={<AuthPage mode="signup" />} />
          <Route path="/devenir-partenaire" element={<PartnerSignupPage />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/conditions" element={<LegalPage doc="cgu" />} />
          <Route path="/confidentialite" element={<LegalPage doc="privacy" />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
          <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
          <Route path="/dashboard" element={<DashboardLayout />}>
            <Route index element={<DashboardHome />} />
            <Route path="parcours" element={<MonParcours />} />
            <Route path="documents" element={<MesDocuments />} />
            <Route path="echeances" element={<MesEcheances />} />
            <Route path="parametres" element={<Parametres />} />
            <Route path="coach" element={<ChatAgent agent="coach" title="Coach Civique IA" subtitle="Valeurs, histoire, institutions et préparation à l'examen civique." icon={GraduationCap} accent={COACH_ACCENT} suggestions={["Quelles sont les valeurs de la République ?", "Explique-moi la laïcité simplement", "Fais-moi un quiz sur les institutions", "Comment se passe l'entretien d'assimilation ?"]} />} />
            <Route path="examen" element={<ExamenQuiz />} />
            <Route path="assistant" element={<ChatAgent agent="demarches" title="Assistant Démarches IA" subtitle="Vos démarches expliquées pas à pas : documents, organismes, liens officiels." icon={Bot} accent={DEMARCHES_ACCENT} suggestions={["Mon titre de séjour expire bientôt, que faire ?", "Quels documents pour une carte de résident ?", "Comment renouveler mon récépissé sur l'ANEF ?", "Je viens de changer d'adresse, quelles démarches ?"]} />} />
            <Route path="ressources" element={<Formations />} />
            <Route path="interprete" element={<Interprete />} />
            <Route path="renouvellement" element={<GuideRenouvellement />} />
            <Route path="partenaires" element={<Partenaires />} />
            <Route path="partenaire" element={<EspacePartenaire />} />
            <Route path="messages" element={<Messages />} />
          </Route>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminOverview />} />
            <Route path="utilisateurs" element={<AdminUsers />} />
            <Route path="premium" element={<AdminPremium />} />
            <Route path="contenus" element={<AdminContents />} />
            <Route path="questions" element={<AdminQuestions />} />
            <Route path="partenaires" element={<AdminPartners />} />
            <Route path="journal" element={<AdminAudit />} />
            <Route path="suggestions" element={<AdminSuggestions />} />
            <Route path="ia" element={<AdminAI />} />
            <Route path="moderation" element={<AdminModeration />} />
            <Route path="securite" element={<AdminSecurity />} />
            <Route path="parametres" element={<AdminSettings />} />
          </Route>
        </Routes>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
